<script setup lang="ts">
import { Building2 } from '@lucide/vue'
import { Avatar, AvatarFallback, AvatarImage } from '@zerohuman/ui'
import type { HTMLAttributes } from 'vue'
import { initials } from '@/lib/avatar'

/**
 * An enterprise's picture, or its initials, or a building when even a name is missing. Its shape is the
 * kit Avatar's own, square like every corner in the theme (BG §17.2): a company mark is a logo, not a face.
 * Takes anything with a name and an avatarUrl, so `me.enterprise`, a picker card and a map circle all fit.
 */
const props = defineProps<{
    enterprise: { name: string; avatarUrl?: string | null }
    class?: HTMLAttributes['class']
    /** Size of the initials, e.g. `text-xl` for a large avatar. */
    fallbackClass?: HTMLAttributes['class']
}>()
</script>

<template>
	<Avatar :class="props.class" data-testid="enterprise-avatar">
		<AvatarImage v-if="enterprise.avatarUrl" :src="enterprise.avatarUrl" :alt="enterprise.name" />
		<AvatarFallback v-else :aria-label="enterprise.name" :class="fallbackClass">
			<template v-if="initials(enterprise.name)">{{ initials(enterprise.name) }}</template>
			<Building2 v-else class="size-1/2" aria-hidden="true" />
		</AvatarFallback>
	</Avatar>
</template>
